import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { CopilotAction } from './types';

interface CopilotActionContextValue {
  currentAction: CopilotAction | null;
  onCopilotAction: (action: CopilotAction) => void;
  clearAction: () => void;
}

const CopilotActionContext = createContext<
  CopilotActionContextValue | undefined
>(undefined);

interface CopilotActionProviderProps {
  children: React.ReactNode;
  /** Auto-clear action after this many milliseconds (default: 5000) */
  autoClearTimeout?: number;
}

export const CopilotActionProvider: React.FC<CopilotActionProviderProps> = ({
  children,
  autoClearTimeout = 5000,
}) => {
  const [currentAction, setCurrentAction] = useState<CopilotAction | null>(
    null
  );

  const onCopilotAction = useCallback((action: CopilotAction) => {
    setCurrentAction(action);
  }, []);

  const clearAction = useCallback(() => {
    setCurrentAction(null);
  }, []);

  useEffect(() => {
    if (currentAction && autoClearTimeout > 0) {
      const timer = setTimeout(() => {
        setCurrentAction(null);
      }, autoClearTimeout);

      return () => clearTimeout(timer);
    }
  }, [currentAction, autoClearTimeout]);

  const value = useMemo(
    () => ({
      currentAction,
      onCopilotAction,
      clearAction,
    }),
    [currentAction, onCopilotAction, clearAction]
  );

  return (
    <CopilotActionContext.Provider value={value}>
      {children}
    </CopilotActionContext.Provider>
  );
};

export const useCopilotAction = (): CopilotActionContextValue | null => {
  const context = useContext(CopilotActionContext);

  if (!context) {
    return null;
  }

  return context;
};
